import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const FoodicsCallback = () => {
  const { t, i18n } = useTranslation('translation');
  const isArabic = i18n.language === 'ar';
  const location = useLocation();
  const navigate = useNavigate();
  const [integrationStatus, setIntegrationStatus] = useState('integrating'); // 'integrating', 'success'
  const hasFetched = useRef(false); // Prevent duplicate API calls in dev StrictMode

  const getLocalRedirectHost = () => {
    return process.env.REACT_APP_FOODICS_LOCAL_REDIRECT_HOST || '';
  };

  const isStagingCallbackHost = () => {
    const stagingHosts = ['onehub.staging.loops.sa', 'onehub.demo.loops.sa'];
    return stagingHosts.includes(window.location.hostname);
  };

  const redirectToLocalIfStaging = () => {
    const localHost = getLocalRedirectHost();
    if (!localHost || !isStagingCallbackHost()) return false;

    const query = window.location.search || '';
    const shouldRedirect = query.includes('state=') || query.includes('error=') || query.includes('code=');
    if (!shouldRedirect) return false;

    const targetUrl = `${localHost}/redirect-foodics${query}`;
    window.location.replace(targetUrl);
    return true;
  };

  const getAppIdFromState = (rawState) => {
    if (!rawState) return '';
    try {
      const parsed = JSON.parse(rawState);
      return parsed?.appId || parsed?.storeId || rawState;
    } catch {
      return rawState;
    }
  };

  useEffect(() => {
    if (redirectToLocalIfStaging()) return;

    if (hasFetched.current) return; // Skip if already run
    hasFetched.current = true;

    const queryParams = new URLSearchParams(location.search);
    const authCode = queryParams.get('code');
    const stateParam = queryParams.get('state');
    const errorParam =
      queryParams.get('error') ||
      queryParams.get('denied') ||
      queryParams.get('error_reason');
    const errorDescription =
      queryParams.get('error_description') ||
      queryParams.get('message') ||
      queryParams.get('error_message');

    const appIdFromState = getAppIdFromState(stateParam);
    const redirectPath = appIdFromState ? `/add-app/${appIdFromState}` : '/apps-management';

    if ((!authCode || !stateParam) && (errorParam || errorDescription)) {
      const deniedMessage =
        errorDescription ||
        (errorParam === 'access_denied'
          ? t('Foodics authorization denied. Please try again.')
          : t('Foodics authorization was denied.'));

      navigate(redirectPath, {
        state: {
          errorMessage: deniedMessage,
        },
      });
      return;
    }

    if (authCode && stateParam) {
      const token = localStorage.getItem('token');
      const codeReceivedAt = new Date().toISOString();
      fetch(`${process.env.REACT_APP_BASE_URL}/v2/foodics/auth/token`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth_code: authCode,
          state: stateParam,
          code_received_at: codeReceivedAt,
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setIntegrationStatus('success');
            toast.success(t('Foodics integration completed successfully!'), {
              position: 'top-right',
              autoClose: 3000,
            });
            // Wait 2 seconds after showing success, then redirect
            setTimeout(() => {
              navigate(redirectPath);
            }, 2000);
          } else {
            console.error('Foodics integration failed:', data.message);
            navigate(redirectPath, {
              state: {
                errorMessage: data.message || t('Foodics integration failed'),
              },
            });
          }
        })
        .catch((error) => {
          console.error('[FOODICS] Integration error:', error);
          navigate(redirectPath, {
            state: {
              errorMessage: t('Network error during Foodics integration. Please check your connection.'),
            },
          });
        });
    } else if (!authCode || !stateParam) {
      console.error('[FOODICS] Missing required parameters:', { authCode, stateParam });
      navigate(redirectPath, { state: { errorMessage: t('Foodics authorization failed. Please try again.') } });
    }
  }, [location.search, navigate, t]);

  return (
    <div
      dir={isArabic ? 'rtl' : 'ltr'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'transparent',
        fontFamily: isArabic ? '"Noto Sans Arabic", "Cairo", -apple-system, sans-serif' : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '20px'
      }}
    >
      <style>
        {`
          @keyframes modernSpin {
            0% { transform: rotate(0deg) scale(1); }
            50% { transform: rotate(180deg) scale(1.1); }
            100% { transform: rotate(360deg) scale(1); }
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 1; }
          }
          
          @keyframes slideUp {
            0% { transform: translateY(20px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          
          @keyframes successBounce {
            0% { transform: scale(0) rotate(0deg); }
            60% { transform: scale(1.3) rotate(180deg); }
            80% { transform: scale(0.9) rotate(360deg); }
            100% { transform: scale(1) rotate(360deg); }
          }
          
          @keyframes checkDraw {
            0% { stroke-dashoffset: 100; }
            100% { stroke-dashoffset: 0; }
          }
          
          @keyframes glowPulse {
            0%, 100% { box-shadow: 0 0 20px rgba(255, 107, 53, 0.3); }
            50% { box-shadow: 0 0 40px rgba(255, 107, 53, 0.6), 0 0 60px rgba(255, 107, 53, 0.3); }
          }
        `}
      </style>

      {integrationStatus === 'integrating' && (
        <div style={{ textAlign: 'center', animation: 'slideUp 0.5s ease-out' }}>
          <div style={{
            position: 'relative',
            marginBottom: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '80px',
            height: '80px',
            margin: '0 auto 40px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              border: '3px solid rgba(255, 107, 53, 0.2)',
              borderTop: '3px solid #FF6B35',
              borderRadius: '50%',
              animation: 'modernSpin 2s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite',
              position: 'absolute',
              top: '0',
              left: '0'
            }}></div>
            <div style={{
              width: '60px',
              height: '60px',
              background: 'linear-gradient(45deg, #FF6B35, #FFB830)',
              borderRadius: '50%',
              animation: 'pulse 1.5s ease-in-out infinite',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 30px rgba(255, 107, 53, 0.30)',
              position: 'absolute',
              top: '10px',
              left: '10px'
            }}>
              <div style={{
                width: '20px',
                height: '20px',
                background: 'white',
                borderRadius: '50%',
                opacity: 0.8
              }}></div>
            </div>
          </div>
          <div style={{
            fontSize: '24px',
            color: '#000',
            fontWeight: '300',
            marginBottom: '10px',
            letterSpacing: isArabic ? '0' : '0.5px'
          }}>
            {t('Integrating with Loops')}
          </div>
          <div style={{
            fontSize: '16px',
            color: '#000',
            fontWeight: '300'
          }}>
            {t('Please wait while we connect your account...')}
          </div>
        </div>
      )}

      {integrationStatus === 'success' && (
        <div style={{ textAlign: 'center', animation: 'slideUp 0.6s ease-out' }}>
          <div style={{
            width: '100px',
            height: '100px',
            background: 'linear-gradient(135deg, #4CAF50, #45a049)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '30px',
            animation: 'successBounce 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55), glowPulse 2s ease-in-out infinite',
            position: 'relative',
            margin: '0 auto 30px'
          }}>
            <svg width="50" height="50" viewBox="0 0 24 24" fill="none">
              <path
                d="M7 13l3 3 7-7"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="100"
                strokeDashoffset="0"
                style={{
                  animation: 'checkDraw 0.6s ease-out 0.3s both'
                }}
              />
            </svg>
          </div>
          <div style={{
            fontSize: '28px',
            color: '#4CAF50',
            fontWeight: '500',
            marginBottom: '10px',
            letterSpacing: isArabic ? '0' : '0.5px'
          }}>
            {t('Success!')}
          </div>
          <div style={{
            fontSize: '16px',
            color: '#000',
            fontWeight: '300'
          }}>
            {t('Your Foodics account has been successfully connected')}
          </div>
          <div style={{
            fontSize: '14px',
            color: '#888',
            marginTop: '20px',
            fontWeight: '300'
          }}>
            {t('Redirecting you back...')}
          </div>
        </div>
      )}
    </div>
  );
};

export default FoodicsCallback;

